/**
 * Card primitive — glasskortet fra den grønne handoffen (#152).
 *
 * Varianter:
 *   - default: glass — `bg-card` + 1 px `border-card-line` + `backdrop-blur-card`
 *              (20 px), radius 24 og kortskyggen. Skyggen er `shadow-md`, som
 *              dusk.css nuller ut, så mørkt tema er uten skygge
 *              (--stell-card-shadow: none).
 *   - soft:    samme glass uten skygge, for kort som ligger tett i en liste
 *              eller rundt en tilstandsprimitiv (EmptyState, ErrorState).
 *   - inset:   tallboksen inne i et kort (`--stell-inset`), radius 16, uten
 *              kant og uten blur — den ligger allerede på glass.
 *
 * Padding er standard 20 px (inset: 12 px) og kan overstyres med className,
 * slik kallstedene gjør i dag.
 */
import type { ReactNode } from 'react';

type CardVariant = 'default' | 'soft' | 'inset';

/** Glasset uten kant — TaskRow legger på sin egen kantfarge (#154). */
export const GLASS_FILL = 'bg-card backdrop-blur-card';

/** Glassflaten uten form og padding — delt med TaskRow. */
export const GLASS_SURFACE = `${GLASS_FILL} border border-card-line`;

const GLASS = `${GLASS_SURFACE} rounded-lg p-5`;

const VARIANT_CLASS: Record<CardVariant, string> = {
  default: `${GLASS} shadow-md`,
  soft: GLASS,
  inset: 'bg-inset rounded-md p-3',
};

export function Card({
  variant = 'default',
  children,
  className,
  onClick,
  'aria-label': ariaLabel,
}: {
  variant?: CardVariant;
  children: ReactNode;
  className?: string;
  onClick?: () => void;
  /** Kort navn på et klikkbart kort; ellers blir navnet hele innholdet (#160). */
  'aria-label'?: string;
}) {
  return (
    <div
      className={[VARIANT_CLASS[variant], className].filter(Boolean).join(' ')}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      aria-label={ariaLabel}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
    >
      {children}
    </div>
  );
}
